import fs from 'node:fs'
import path from 'node:path'

import { runVercel } from './runner.js'
import type { VercelProject, VercelRunner } from './types.js'

/**
 * Sanitize a project name to Vercel's rules: lowercase, only
 * `[a-z0-9._-]`, no leading/trailing separators, max 100 chars.
 */
export function sanitizeVercelProjectName(name: string): string {
  const cleaned = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[-_.]+/, '')
    .replace(/[-_.]+$/, '')
    .slice(0, 100)
  return cleaned || 'my-app'
}

/**
 * Create a Vercel project and link the local directory to it (writes
 * `.vercel/project.json`). An existing project of the same name is tolerated —
 * we proceed straight to linking. The project id is read back from the linked
 * `.vercel/project.json`, the canonical source of truth.
 */
export async function createVercelProject(
  runner: VercelRunner,
  cwd: string,
  name: string,
  env?: NodeJS.ProcessEnv
): Promise<VercelProject> {
  const projectName = sanitizeVercelProjectName(name)

  await runVercel(runner, ['projects', 'add', projectName], {
    cwd,
    mode: 'capture',
    env
  })
  // A failure here is non-fatal: the most common cause is "already exists",
  // in which case linking below still succeeds.

  const link = await runVercel(runner, ['link', '--project', projectName, '--yes'], {
    cwd,
    mode: 'capture',
    env
  })

  return {
    name: projectName,
    linked: link.success,
    projectId: readLinkedProjectId(cwd)
  }
}

function readLinkedProjectId(cwd: string): string | undefined {
  try {
    const projectJsonPath = path.join(cwd, '.vercel', 'project.json')
    if (!fs.existsSync(projectJsonPath)) return undefined
    const parsed = JSON.parse(fs.readFileSync(projectJsonPath, 'utf-8')) as {
      projectId?: string
    }
    return parsed.projectId
  } catch {
    return undefined
  }
}
